"use client";

import { api } from "@v1/backend/convex/_generated/api";
import type { Id } from "@v1/backend/convex/_generated/dataModel";
import { useMutation } from "convex/react";
import { useEffect } from "react";
import { isChatUnread, isTicketUnread, useMessenger, useMessengerSurface } from "./messenger-context";

/** While a chat or ticket is open, its unread dot clears (also for replies that arrive now). */
export function useMessengerMarkRead(
  item:
    | { readonly kind: "chat"; readonly sessionId: Id<"public_chat_sessions"> }
    | { readonly kind: "ticket"; readonly taskId: Id<"tasks"> }
    | null,
): void {
  const { unread } = useMessenger();
  const surface = useMessengerSurface();
  const markRead = useMutation(api.customerChat.messenger_writes.markRead);
  const kind = item?.kind ?? null;
  const id = item === null ? null : item.kind === "chat" ? item.sessionId : item.taskId;
  const isUnread =
    item === null
      ? false
      : item.kind === "chat"
        ? isChatUnread(unread, item.sessionId)
        : isTicketUnread(unread, item.taskId);

  useEffect(() => {
    if (!isUnread || item === null) return;
    void markRead({
      surface,
      item: item.kind === "chat" ? { kind: "chat", session_id: item.sessionId } : { kind: "ticket", task_id: item.taskId },
    }).catch(() => undefined);
    // `item` is rebuilt each render; kind + id carry its identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUnread, kind, id, markRead, surface]);
}
